// frontend/src/components/StaffForm.tsx

import React, { useState, FormEvent } from "react";
import {
  X,
  UserRound,
  Mail,
  MapPin,
  Hash,
  BadgeDollarSign,
  CalendarDays,
} from "lucide-react";
import { CreateStaffRequest } from "../types/staff";

interface Props {
  onSubmit: (data: CreateStaffRequest) => Promise<void>;
  onCancel: () => void;
}

export default function StaffForm({
  onSubmit,
  onCancel,
}: Props) {
  // keep salary as string so we can control the input
  const [form, setForm] = useState<
    Omit<CreateStaffRequest, "salary"> & { salary: string }
  >({
    name: "",
    email: "",
    address: "",
    panNumber: "",
    salary: "",
    joinedDate: new Date().toISOString().slice(0, 10),
  });

  const [error, setError] = useState("");

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "salary") {
      // allow only digits
      if (!/^\d*$/.test(value)) return;

      setForm((prev) => ({
        ...prev,
        salary: value,
      }));
    } else {
      setForm(
        (prev) =>
          ({
            ...prev,
            [name]: value,
          }) as any,
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await onSubmit({
        name: form.name,
        email: form.email,
        address: form.address,
        panNumber:
          form.panNumber.trim() === ""
            ? "null"
            : form.panNumber.trim(),
        salary: Number(form.salary),
        joinedDate: form.joinedDate,
      });
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message,
      );
    }
  };

  const fieldClass = `
    box-border h-11 w-full min-w-0 rounded-[10px]
    border border-slate-200 bg-white px-3.5
    text-[13px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-emerald-400
    focus:ring-2 focus:ring-emerald-100
  `;

  const labelClass = `
    mb-1.5 block
    text-[10px] font-semibold uppercase
    tracking-[0.05em] text-slate-500
  `;

  const iconClass =
    "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400";

  return (
    <div
      className="
        fixed inset-0 z-[1600]
        flex items-center justify-center
        bg-slate-950/45 px-4 py-6
        backdrop-blur-[2px]
      "
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-form-title"
        className="
          flex max-h-[92vh] w-full max-w-[760px]
          flex-col overflow-hidden
          rounded-[18px]
          border border-slate-200
          bg-white
          shadow-[0_24px_70px_rgba(15,23,42,0.22)]
        "
      >
        {/* Header */}
        <div
          className="
            flex shrink-0 items-start justify-between gap-4
            border-b border-slate-100
            px-6 py-5
            max-sm:px-4
          "
        >
          <div className="min-w-0">
            <div
              className="
                mb-1 text-[10px] font-bold uppercase
                tracking-[0.12em] text-blue-600
              "
            >
              Staff management
            </div>

            <h2
              id="staff-form-title"
              className="
                m-0 text-[20px] font-bold
                tracking-[-0.02em] text-slate-950
              "
            >
              Add Staff
            </h2>

            <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
              Enter staff details to create a new staff record.
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            title="Close"
            aria-label="Close"
            className="
              inline-flex h-9 w-9 shrink-0 items-center justify-center
              rounded-[9px]
              border border-emerald-600
              bg-emerald-600
              text-white transition
              hover:border-emerald-700
              hover:bg-emerald-700
              focus:outline-none
              focus:ring-2
              focus:ring-emerald-200
            "
          >
            <X size={17} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            className="
              mx-6 mt-4 rounded-[10px]
              border border-red-200 bg-red-50
              px-4 py-3
              text-[12px] font-medium text-red-700
              max-sm:mx-4
            "
          >
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div
            className="
              grid grid-cols-1 gap-x-4 gap-y-4
              px-6 py-5
              sm:grid-cols-2
              max-sm:px-4
            "
          >
            {/* Name */}
            <label className="min-w-0">
              <span className={labelClass}>Name</span>

              <div className="relative">
                <UserRound size={15} className={iconClass} />

                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleFieldChange}
                  required
                  placeholder="Enter full name"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Email */}
            <label className="min-w-0">
              <span className={labelClass}>Email</span>

              <div className="relative">
                <Mail size={15} className={iconClass} />

                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleFieldChange}
                  required
                  placeholder="Enter email address"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Address */}
            <label className="min-w-0">
              <span className={labelClass}>Address</span>

              <div className="relative">
                <MapPin size={15} className={iconClass} />

                <input
                  id="address"
                  name="address"
                  type="text"
                  value={form.address}
                  onChange={handleFieldChange}
                  required
                  placeholder="Enter address"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* PAN */}
            <label className="min-w-0">
              <span className={labelClass}>PAN Number</span>

              <div className="relative">
                <Hash size={15} className={iconClass} />

                <input
                  id="panNumber"
                  name="panNumber"
                  type="text"
                  value={form.panNumber}
                  onChange={handleFieldChange}
                  placeholder="Enter PAN number"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Salary */}
            <label className="min-w-0">
              <span className={labelClass}>Salary</span>

              <div className="relative">
                <BadgeDollarSign size={15} className={iconClass} />

                <input
                  id="salary"
                  name="salary"
                  type="text"
                  value={form.salary}
                  onChange={handleFieldChange}
                  placeholder="Enter salary"
                  required
                  inputMode="numeric"
                  pattern="\d*"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Joined Date */}
            <label className="min-w-0">
              <span className={labelClass}>Joined Date</span>

              <div className="relative">
                <CalendarDays size={15} className={iconClass} />

                <input
                  id="joinedDate"
                  name="joinedDate"
                  type="date"
                  value={form.joinedDate}
                  onChange={handleFieldChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>
          </div>

          {/* Footer */}
          <div
            className="
              flex items-center justify-end gap-2
              border-t border-slate-100
              bg-slate-50/50
              px-6 py-4
              max-sm:px-4
            "
          >
            <button
              type="button"
              onClick={onCancel}
              className="
                inline-flex h-10 items-center justify-center
                rounded-[9px]
                border border-emerald-200
                bg-white px-4
                text-[12px] font-semibold text-emerald-700
                transition
                hover:border-emerald-300
                hover:bg-emerald-50
                hover:text-emerald-800
                focus:outline-none
                focus:ring-2
                focus:ring-emerald-200
              "
            >
              Cancel
            </button>

            <button
              type="submit"
              className="
                inline-flex h-10 min-w-[104px]
                items-center justify-center
                rounded-[9px]
                border border-emerald-600
                bg-emerald-600 px-4
                text-[12px] font-semibold text-white
                transition
                hover:border-emerald-700
                hover:bg-emerald-700
                focus:outline-none
                focus:ring-2
                focus:ring-emerald-200
              "
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
